'use client';

import Link from 'next/link';
import { useEffect, useRef, useState, type ChangeEvent } from 'react';
import { createCanvas, loadRasterImage } from '@/lib/browser-image-tools';
import { readVideoMetadata } from '@/lib/browser-video-tools';

type HelperMode = 'triage' | 'estimate' | 'scan' | 'duplicates' | 'routes' | 'plan';
type Platform = 'iphone' | 'android';
type Urgency = 'steady' | 'tight' | 'critical';
type PlanningStyle = 'low-risk' | 'balanced';
type PressurePointKey =
  | 'duplicates'
  | 'screenshots'
  | 'large-videos'
  | 'downloads'
  | 'chat-media'
  | 'contacts';
type EstimateKey = 'duplicates' | 'screenshots' | 'largeVideos' | 'downloads';

type PressurePoint = {
  id: PressurePointKey;
  label: string;
  description: string;
  iphoneWeight: number;
  androidWeight: number;
  fastWin: boolean;
  actionLabel: string;
};

type EstimateDefinition = {
  id: EstimateKey;
  label: string;
  hint: string;
  unitLabel: string;
  min: number;
  max: number;
  step: number;
  iphoneMb: number;
  androidMb: number;
};

type PlanningPass = {
  id: string;
  label: string;
  minutes: number;
  estimatedGb: number;
  detail: string;
  lowRisk: boolean;
};

type ScannedMediaItem = {
  id: string;
  fileName: string;
  size: number;
  kind: 'image' | 'video';
  width: number;
  height: number;
  durationMs: number | null;
  isLikelyScreenshot: boolean;
  duplicateKey: string | null;
};

type ScanSummary = {
  totalCount: number;
  totalBytes: number;
  skippedCount: number;
  imageCount: number;
  videoCount: number;
  screenshotCount: number;
  screenshotBytes: number;
  largeVideoCount: number;
  largeVideoBytes: number;
  possibleDuplicateCount: number;
  possibleDuplicateBytes: number;
  duplicateGroups: Array<{
    key: string;
    items: ScannedMediaItem[];
    recoverableBytes: number;
  }>;
  topItems: ScannedMediaItem[];
  primaryFocus: string;
  primaryReason: string;
  nextAction: string;
};

type DuplicateReviewItem = {
  id: string;
  fileName: string;
  size: number;
  width: number;
  height: number;
  hashBits: number[];
  previewUrl: string;
};

type DuplicateReviewGroup = {
  id: string;
  items: DuplicateReviewItem[];
  estimatedRecoverableBytes: number;
  averageDistance: number;
};

type DuplicateReviewSummary = {
  totalCount: number;
  skippedCount: number;
  groupCount: number;
  groupedItemCount: number;
  estimatedRecoverableBytes: number;
  groups: DuplicateReviewGroup[];
};

type DeviceCleanupRoute = {
  id: PressurePointKey | 'settings-overview';
  label: string;
  location: string;
  detail: string;
  safety: 'low-risk' | 'review';
};

const helperModes: Array<{ id: HelperMode; label: string; description: string }> = [
  {
    id: 'triage',
    label: '1. Start here',
    description: 'See what to clean first.',
  },
  {
    id: 'estimate',
    label: '2. Estimate',
    description: 'See where space may come from.',
  },
  {
    id: 'scan',
    label: '3. Scan files',
    description: 'Check a small batch of files.',
  },
  {
    id: 'duplicates',
    label: '4. Duplicates',
    description: 'Find repeated photos.',
  },
  {
    id: 'routes',
    label: '5. Phone steps',
    description: 'Open the right places on your phone.',
  },
  {
    id: 'plan',
    label: '6. Plan',
    description: 'Get a short cleanup order.',
  },
];

const platformOptions: Array<{ id: Platform; label: string; description?: string }> = [
  {
    id: 'iphone',
    label: 'iPhone',
  },
  {
    id: 'android',
    label: 'Android',
  },
];

const urgencyOptions: Array<{ id: Urgency; label: string; description: string }> = [
  {
    id: 'steady',
    label: 'Not urgent',
    description: 'You can do this slowly.',
  },
  {
    id: 'tight',
    label: 'Storage is tight',
    description: 'The phone is starting to feel full.',
  },
  {
    id: 'critical',
    label: 'Storage is full',
    description: 'You need space back now.',
  },
];

const planningStyles: Array<{ id: PlanningStyle; label: string; description: string }> = [
  {
    id: 'low-risk',
    label: 'Easy first',
    description: 'Start with the safest things to remove.',
  },
  {
    id: 'balanced',
    label: 'More space',
    description: 'Mix easy cleanup with bigger wins.',
  },
];

const pressurePoints: PressurePoint[] = [
  {
    id: 'duplicates',
    label: 'Duplicate photos',
    description: 'Repeated shots and copies.',
    iphoneWeight: 5.2,
    androidWeight: 3.6,
    fastWin: false,
    actionLabel: 'Check repeated photos before deleting one by one.',
  },
  {
    id: 'screenshots',
    label: 'Screenshots',
    description: 'Old screenshots and screen recordings.',
    iphoneWeight: 4.4,
    androidWeight: 5.1,
    fastWin: true,
    actionLabel: 'Delete old screenshots in one pass.',
  },
  {
    id: 'large-videos',
    label: 'Large videos',
    description: 'A few big videos can take a lot of space.',
    iphoneWeight: 4.9,
    androidWeight: 4.5,
    fastWin: true,
    actionLabel: 'Check your biggest videos first.',
  },
  {
    id: 'downloads',
    label: 'Downloads',
    description: 'Old saved files and exports.',
    iphoneWeight: 2.2,
    androidWeight: 4.8,
    fastWin: true,
    actionLabel: 'Open Downloads before cleaning your photo library.',
  },
  {
    id: 'chat-media',
    label: 'Chat media',
    description: 'Saved photos and videos from chats.',
    iphoneWeight: 3.4,
    androidWeight: 4.2,
    fastWin: true,
    actionLabel: 'Clear saved chat media if it keeps piling up.',
  },
  {
    id: 'contacts',
    label: 'Contacts',
    description: 'Useful later, but not a big space saver.',
    iphoneWeight: 1.3,
    androidWeight: 1.8,
    fastWin: false,
    actionLabel: 'Leave contacts for last.',
  },
];

const estimateDefinitions: EstimateDefinition[] = [
  {
    id: 'duplicates',
    label: 'Duplicate or near-duplicate photos',
    hint: 'Use this for repeated photos you may want to remove.',
    unitLabel: 'items',
    min: 0,
    max: 1500,
    step: 25,
    iphoneMb: 3.2,
    androidMb: 2.8,
  },
  {
    id: 'screenshots',
    label: 'Screenshots and low-value captures',
    hint: 'Usually one of the easiest things to clean up.',
    unitLabel: 'items',
    min: 0,
    max: 2500,
    step: 25,
    iphoneMb: 1.4,
    androidMb: 1.1,
  },
  {
    id: 'largeVideos',
    label: 'Large videos',
    hint: 'A few big videos can free a lot of space.',
    unitLabel: 'videos',
    min: 0,
    max: 120,
    step: 1,
    iphoneMb: 180,
    androidMb: 160,
  },
  {
    id: 'downloads',
    label: 'Saved files, downloads, or chat media',
    hint: 'Use this for downloads, saved files, and chat media.',
    unitLabel: 'items',
    min: 0,
    max: 1200,
    step: 25,
    iphoneMb: 5.5,
    androidMb: 8.5,
  },
];

const initialEstimateInputs: Record<EstimateKey, number> = {
  duplicates: 180,
  screenshots: 260,
  largeVideos: 18,
  downloads: 120,
};

const maxScanFiles = 40;
const maxDuplicateReviewFiles = 24;
const largeVideoThresholdBytes = 100 * 1024 * 1024;
const duplicateHashThreshold = 7;

const urgencyLabels: Record<Urgency, string> = {
  steady: 'Not urgent',
  tight: 'Storage is tight',
  critical: 'Storage is full',
};

function formatBytes(size: number) {
  if (size < 1024) {
    return `${size} B`;
  }

  if (size < 1024 * 1024) {
    return `${(size / 1024).toFixed(1)} KB`;
  }

  if (size < 1024 * 1024 * 1024) {
    return `${(size / (1024 * 1024)).toFixed(1)} MB`;
  }

  return `${(size / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

function formatStorage(gigabytes: number) {
  if (gigabytes < 1) {
    return `${Math.round(gigabytes * 1024)} MB`;
  }

  return `${gigabytes >= 10 ? gigabytes.toFixed(0) : gigabytes.toFixed(1)} GB`;
}

function formatCount(value: number) {
  return value.toLocaleString();
}

function formatDuration(durationMs: number | null) {
  if (!durationMs || durationMs <= 0) {
    return null;
  }

  const totalSeconds = Math.round(durationMs / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  if (minutes <= 0) {
    return `${seconds}s`;
  }

  return `${minutes}m ${seconds.toString().padStart(2, '0')}s`;
}

function formatPlatformLabel(platform: Platform) {
  return platform === 'iphone' ? 'iPhone' : 'Android';
}

function normalizeDuplicateStem(fileName: string) {
  return fileName
    .toLowerCase()
    .replace(/\.[^.]+$/, '')
    .replace(/\bcopy\b/g, '')
    .replace(/\(\d+\)$/g, '')
    .replace(/-\d+$/g, '')
    .replace(/[-_\s]+/g, ' ')
    .trim();
}

function computeAverageHashBits(image: CanvasImageSource) {
  const canvas = createCanvas(8, 8);
  const context = canvas.getContext('2d');

  if (!context) {
    throw new Error('The browser could not prepare duplicate review for this image.');
  }

  context.drawImage(image, 0, 0, 8, 8);
  const { data } = context.getImageData(0, 0, 8, 8);
  const grayscaleValues: number[] = [];

  for (let index = 0; index < data.length; index += 4) {
    const red = data[index] ?? 0;
    const green = data[index + 1] ?? 0;
    const blue = data[index + 2] ?? 0;
    grayscaleValues.push(red * 0.299 + green * 0.587 + blue * 0.114);
  }

  const average =
    grayscaleValues.reduce((total, value) => total + value, 0) /
    Math.max(1, grayscaleValues.length);

  return grayscaleValues.map((value) => (value >= average ? 1 : 0));
}

function calculateHammingDistance(left: number[], right: number[]) {
  let distance = 0;

  for (let index = 0; index < Math.min(left.length, right.length); index += 1) {
    if (left[index] !== right[index]) {
      distance += 1;
    }
  }

  return distance + Math.abs(left.length - right.length);
}

function getAspectRatio(width: number, height: number) {
  return width > 0 && height > 0 ? width / height : 1;
}

function revokeDuplicatePreviewUrls(summary: DuplicateReviewSummary | null) {
  if (!summary) {
    return;
  }

  for (const group of summary.groups) {
    for (const item of group.items) {
      URL.revokeObjectURL(item.previewUrl);
    }
  }
}

function inferScreenshotByName(fileName: string) {
  return /(screenshot|screen shot|screen-shot|screen_shot|screencap|screen capture)/i.test(
    fileName,
  );
}

function buildDuplicateKey(file: File) {
  const normalizedStem = normalizeDuplicateStem(file.name);

  if (normalizedStem.length < 4) {
    return null;
  }

  return `${normalizedStem}::${file.size}`;
}

function getDeviceCleanupRoutes(platform: Platform): DeviceCleanupRoute[] {
  if (platform === 'iphone') {
    return [
      {
        id: 'settings-overview',
        label: 'Open iPhone Storage first',
        location: 'Settings > General > iPhone Storage',
        detail: 'Start here first so you know what is taking space.',
        safety: 'low-risk',
      },
      {
        id: 'large-videos',
        label: 'Check large videos',
        location: 'Photos > Media Types > Videos',
        detail: 'Big videos are often the fastest way to free up space.',
        safety: 'low-risk',
      },
      {
        id: 'screenshots',
        label: 'Clear screenshot clutter',
        location: 'Photos > Media Types > Screenshots',
        detail: 'Screenshots are usually safe and easy to remove.',
        safety: 'low-risk',
      },
      {
        id: 'duplicates',
        label: 'Check duplicate photos',
        location: 'Photos > Utilities > Duplicates',
        detail: 'Use the built-in duplicate list to merge repeated photos.',
        safety: 'review',
      },
      {
        id: 'downloads',
        label: 'Check saved downloads',
        location: 'Files > Browse > Downloads',
        detail: 'Old saved files often stay here longer than you expect.',
        safety: 'low-risk',
      },
      {
        id: 'chat-media',
        label: 'Check chat media',
        location: 'WhatsApp > Settings > Storage and Data > Manage Storage',
        detail: 'Chat photos and videos often build up quietly over time.',
        safety: 'low-risk',
      },
      {
        id: 'contacts',
        label: 'Check contacts last',
        location: 'Contacts > Lists > Duplicates Found',
        detail: 'Helpful later, but not a big space saver.',
        safety: 'review',
      },
    ];
  }

  return [
    {
      id: 'settings-overview',
      label: 'Open Android storage first',
      location: 'Settings > Storage',
      detail: 'Start here first so you know what is taking space.',
      safety: 'low-risk',
    },
    {
      id: 'downloads',
      label: 'Open Downloads',
      location: 'Files or My Files > Downloads',
      detail: 'Old downloads are often the main problem on Android.',
      safety: 'low-risk',
    },
    {
      id: 'screenshots',
      label: 'Clear screenshot clutter',
      location: 'Photos or Gallery > Albums > Screenshots',
      detail: 'Screenshots are usually quick and safe to remove.',
      safety: 'low-risk',
    },
    {
      id: 'large-videos',
      label: 'Review large videos',
      location: 'Files by Google > Browse > Videos',
      detail: 'A few big videos can free more space than many photos.',
      safety: 'low-risk',
    },
    {
      id: 'duplicates',
      label: 'Check duplicate photos',
      location: 'Files by Google > Clean or your gallery duplicate suggestions',
      detail: 'Use duplicate suggestions to clean repeated files faster.',
      safety: 'review',
    },
    {
      id: 'chat-media',
      label: 'Check chat media',
      location: 'WhatsApp > Settings > Storage and data > Manage storage',
      detail: 'Chat photos and videos often build up quietly over time.',
      safety: 'low-risk',
    },
    {
      id: 'contacts',
      label: 'Check contacts last',
      location: 'Google Contacts > Fix & manage > Merge & fix',
      detail: 'Helpful later, but not a big space saver.',
      safety: 'review',
    },
  ];
}

async function scanSelectedMediaFiles(files: File[]) {
  const items: ScannedMediaItem[] = [];
  let skippedCount = 0;

  for (const [index, file] of files.entries()) {
    const duplicateKey = buildDuplicateKey(file);
    const isLikelyScreenshot = inferScreenshotByName(file.name);

    try {
      if (file.type.startsWith('image/')) {
        const image = await loadRasterImage(file);
        items.push({
          id: `${file.name}-${file.size}-${index}`,
          fileName: file.name,
          size: file.size,
          kind: 'image',
          width: image.width,
          height: image.height,
          durationMs: null,
          isLikelyScreenshot,
          duplicateKey,
        });
        continue;
      }

      if (file.type.startsWith('video/')) {
        const metadata = await readVideoMetadata(file);
        items.push({
          id: `${file.name}-${file.size}-${index}`,
          fileName: file.name,
          size: file.size,
          kind: 'video',
          width: metadata.width,
          height: metadata.height,
          durationMs: metadata.durationMs,
          isLikelyScreenshot: false,
          duplicateKey,
        });
      }
    } catch {
      skippedCount += 1;
    }
  }

  const duplicateGroupMap = new Map<string, ScannedMediaItem[]>();

  for (const item of items) {
    if (!item.duplicateKey) {
      continue;
    }

    const current = duplicateGroupMap.get(item.duplicateKey) ?? [];
    current.push(item);
    duplicateGroupMap.set(item.duplicateKey, current);
  }

  const duplicateGroups = [...duplicateGroupMap.entries()]
    .map(([key, groupedItems]) => ({
      key,
      items: groupedItems,
      recoverableBytes:
        groupedItems.length > 1
          ? groupedItems.slice(1).reduce((total, item) => total + item.size, 0)
          : 0,
    }))
    .filter((group) => group.items.length > 1)
    .sort((left, right) => right.recoverableBytes - left.recoverableBytes);

  const screenshotItems = items.filter((item) => item.isLikelyScreenshot);
  const largeVideoItems = items
    .filter((item) => item.kind === 'video' && item.size >= largeVideoThresholdBytes)
    .sort((left, right) => right.size - left.size);

  const screenshotBytes = screenshotItems.reduce((total, item) => total + item.size, 0);
  const largeVideoBytes = largeVideoItems.reduce((total, item) => total + item.size, 0);
  const possibleDuplicateBytes = duplicateGroups.reduce(
    (total, group) => total + group.recoverableBytes,
    0,
  );

  const topItems = [...items].sort((left, right) => right.size - left.size).slice(0, 6);
  const categoryCandidates = [
    {
      id: 'large-videos',
      bytes: largeVideoBytes,
      label: 'Large videos',
      reason: 'Your selected videos look like the biggest space win.',
      action: 'Check the biggest videos first.',
    },
    {
      id: 'screenshots',
      bytes: screenshotBytes,
      label: 'Screenshots',
      reason: 'This batch looks heavy on screenshots.',
      action: 'Clear screenshots in one easy pass.',
    },
    {
      id: 'duplicates',
      bytes: possibleDuplicateBytes,
      label: 'Possible duplicates',
      reason: 'Some selected files look like copies.',
      action: 'Review those copies and keep one version.',
    },
  ].sort((left, right) => right.bytes - left.bytes);

  const primaryCandidate = categoryCandidates[0];
  const totalBytes = items.reduce((total, item) => total + item.size, 0);

  return {
    totalCount: items.length,
    totalBytes,
    skippedCount,
    imageCount: items.filter((item) => item.kind === 'image').length,
    videoCount: items.filter((item) => item.kind === 'video').length,
    screenshotCount: screenshotItems.length,
    screenshotBytes,
    largeVideoCount: largeVideoItems.length,
    largeVideoBytes,
    possibleDuplicateCount: duplicateGroups.reduce((total, group) => total + group.items.length, 0),
    possibleDuplicateBytes,
    duplicateGroups,
    topItems,
    primaryFocus:
      primaryCandidate.bytes > 0 ? primaryCandidate.label : platformAgnosticFallbackFocus(items),
    primaryReason:
      primaryCandidate.bytes > 0
        ? primaryCandidate.reason
        : 'This batch does not show one clear problem, so start with the biggest files first.',
    nextAction:
      primaryCandidate.bytes > 0
        ? primaryCandidate.action
        : 'Start with the biggest files, then check screenshots or duplicates.',
  } satisfies ScanSummary;
}

function platformAgnosticFallbackFocus(items: ScannedMediaItem[]) {
  if (items.some((item) => item.kind === 'video')) {
    return 'Largest selected media';
  }

  return 'Mixed photo cleanup';
}

async function reviewDuplicatePhotos(files: File[]) {
  const reviewItems: DuplicateReviewItem[] = [];
  let skippedCount = 0;

  for (const [index, file] of files.entries()) {
    try {
      const loaded = await loadRasterImage(file);
      reviewItems.push({
        id: `${file.name}-${file.size}-${index}`,
        fileName: file.name,
        size: file.size,
        width: loaded.width,
        height: loaded.height,
        hashBits: computeAverageHashBits(loaded.element),
        previewUrl: URL.createObjectURL(file),
      });
    } catch {
      skippedCount += 1;
    }
  }

  const usedIds = new Set<string>();
  const groups: DuplicateReviewGroup[] = [];

  for (const seed of reviewItems) {
    if (usedIds.has(seed.id)) {
      continue;
    }

    const seedAspectRatio = getAspectRatio(seed.width, seed.height);
    const candidates = [seed];
    const distances: number[] = [];

    for (const candidate of reviewItems) {
      if (candidate.id === seed.id || usedIds.has(candidate.id)) {
        continue;
      }

      const candidateAspectRatio = getAspectRatio(candidate.width, candidate.height);
      const aspectDelta = Math.abs(seedAspectRatio - candidateAspectRatio);

      if (aspectDelta > 0.12) {
        continue;
      }

      const distance = calculateHammingDistance(seed.hashBits, candidate.hashBits);

      if (distance <= duplicateHashThreshold) {
        candidates.push(candidate);
        distances.push(distance);
      }
    }

    if (candidates.length > 1) {
      for (const item of candidates) {
        usedIds.add(item.id);
      }

      const estimatedRecoverableBytes = [...candidates]
        .sort((left, right) => right.size - left.size)
        .slice(1)
        .reduce((total, item) => total + item.size, 0);

      groups.push({
        id: seed.id,
        items: candidates.sort((left, right) => right.size - left.size),
        estimatedRecoverableBytes,
        averageDistance:
          distances.length > 0
            ? distances.reduce((total, value) => total + value, 0) / distances.length
            : 0,
      });
    }
  }

  for (const item of reviewItems) {
    if (!usedIds.has(item.id)) {
      URL.revokeObjectURL(item.previewUrl);
    }
  }

  return {
    totalCount: reviewItems.length,
    skippedCount,
    groupCount: groups.length,
    groupedItemCount: groups.reduce((total, group) => total + group.items.length, 0),
    estimatedRecoverableBytes: groups.reduce(
      (total, group) => total + group.estimatedRecoverableBytes,
      0,
    ),
    groups: groups.sort(
      (left, right) => right.estimatedRecoverableBytes - left.estimatedRecoverableBytes,
    ),
  } satisfies DuplicateReviewSummary;
}

function getPlanningPasses(platform: Platform): PlanningPass[] {
  if (platform === 'iphone') {
    return [
      {
        id: 'large-videos',
        label: 'Check large videos first',
        minutes: 12,
        estimatedGb: 2.1,
        detail: 'A few big videos can free a lot of space fast.',
        lowRisk: true,
      },
      {
        id: 'screenshots',
        label: 'Clear screenshots',
        minutes: 8,
        estimatedGb: 0.8,
        detail: 'Screenshots are usually easy and safe to remove.',
        lowRisk: true,
      },
      {
        id: 'duplicates',
        label: 'Review duplicate photos',
        minutes: 18,
        estimatedGb: 1.6,
        detail: 'Check repeated photos after the easy cleanup is done.',
        lowRisk: false,
      },
      {
        id: 'chat-media',
        label: 'Check chat media',
        minutes: 10,
        estimatedGb: 0.7,
        detail: 'Saved chat photos and videos often build up over time.',
        lowRisk: true,
      },
      {
        id: 'contacts',
        label: 'Check contacts last',
        minutes: 8,
        estimatedGb: 0.1,
        detail: 'Good for cleanup, but not a big space saver.',
        lowRisk: false,
      },
    ];
  }

  return [
    {
      id: 'downloads',
      label: 'Open Downloads',
      minutes: 10,
      estimatedGb: 1.4,
      detail: 'Old downloads are often the main problem on Android.',
      lowRisk: true,
    },
    {
      id: 'screenshots',
      label: 'Clear screenshots',
      minutes: 9,
      estimatedGb: 0.9,
      detail: 'Screenshots are usually quick and safe to remove.',
      lowRisk: true,
    },
    {
      id: 'large-videos',
      label: 'Check large videos',
      minutes: 12,
      estimatedGb: 1.8,
      detail: 'A few big videos can free more space than many photos.',
      lowRisk: true,
    },
    {
      id: 'duplicates',
      label: 'Review duplicate photos',
      minutes: 16,
      estimatedGb: 1.3,
      detail: 'Check repeated photos after the easy cleanup is done.',
      lowRisk: false,
    },
    {
      id: 'contacts',
      label: 'Check contacts last',
      minutes: 8,
      estimatedGb: 0.1,
      detail: 'Good for cleanup, but not a big space saver.',
      lowRisk: false,
    },
  ];
}

export default function BrowserStorageCleanupHelpers() {
  const [mode, setMode] = useState<HelperMode>('triage');
  const [platform, setPlatform] = useState<Platform>('iphone');
  const [urgency, setUrgency] = useState<Urgency>('tight');
  const [selectedPressurePoints, setSelectedPressurePoints] = useState<PressurePointKey[]>([
    'duplicates',
    'large-videos',
  ]);
  const [estimateInputs, setEstimateInputs] =
    useState<Record<EstimateKey, number>>(initialEstimateInputs);
  const [planningStyle, setPlanningStyle] = useState<PlanningStyle>('low-risk');
  const [sessionMinutes, setSessionMinutes] = useState<number>(20);
  const [targetSpaceGb, setTargetSpaceGb] = useState<number>(4);
  const [scanErrorMessage, setScanErrorMessage] = useState<string | null>(null);
  const [scanStatusMessage, setScanStatusMessage] = useState<string | null>(null);
  const [scanSummary, setScanSummary] = useState<ScanSummary | null>(null);
  const [isScanning, setIsScanning] = useState(false);
  const scanInputRef = useRef<HTMLInputElement | null>(null);
  const [duplicateReviewStatusMessage, setDuplicateReviewStatusMessage] = useState<string | null>(
    null,
  );
  const [duplicateReviewErrorMessage, setDuplicateReviewErrorMessage] = useState<string | null>(
    null,
  );
  const [duplicateReviewSummary, setDuplicateReviewSummary] =
    useState<DuplicateReviewSummary | null>(null);
  const [isReviewingDuplicates, setIsReviewingDuplicates] = useState(false);
  const duplicateInputRef = useRef<HTMLInputElement | null>(null);
  const [checklistStatusMessage, setChecklistStatusMessage] = useState<string | null>(null);
  const [checklistErrorMessage, setChecklistErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    return () => {
      revokeDuplicatePreviewUrls(duplicateReviewSummary);
    };
  }, [duplicateReviewSummary]);

  const togglePressurePoint = (id: PressurePointKey) => {
    setSelectedPressurePoints((current) =>
      current.includes(id) ? current.filter((item) => item !== id) : [...current, id],
    );
  };

  const triageRanking = [...pressurePoints]
    .map((point) => {
      const baseWeight = platform === 'iphone' ? point.iphoneWeight : point.androidWeight;
      const urgencyBonus =
        urgency === 'critical' && point.fastWin
          ? 1.35
          : urgency === 'tight' && point.fastWin
            ? 0.6
            : 0;
      const selectionBonus = selectedPressurePoints.includes(point.id) ? 3.8 : 0;

      return {
        ...point,
        score: baseWeight + urgencyBonus + selectionBonus,
      };
    })
    .sort((left, right) => right.score - left.score);

  const primaryFocus = triageRanking[0];
  const secondaryFocuses = triageRanking.slice(1, 3);
  const triageIntro =
    platform === 'iphone'
      ? 'On iPhone, start with photos, screenshots, or big videos before smaller cleanup jobs.'
      : 'On Android, screenshots, downloads, and big videos are usually the best first check.';

  const estimateBreakdown = estimateDefinitions.map((definition) => {
    const count = estimateInputs[definition.id];
    const megabytesPerItem = platform === 'iphone' ? definition.iphoneMb : definition.androidMb;
    const estimatedGigabytes = (count * megabytesPerItem) / 1024;

    return {
      ...definition,
      count,
      estimatedGigabytes,
    };
  });
  const totalEstimatedGigabytes = estimateBreakdown.reduce(
    (total, item) => total + item.estimatedGigabytes,
    0,
  );
  const biggestEstimate = [...estimateBreakdown].sort(
    (left, right) => right.estimatedGigabytes - left.estimatedGigabytes,
  )[0];

  const planningPasses = getPlanningPasses(platform);
  const planningQueue =
    planningStyle === 'low-risk'
      ? planningPasses
          .filter((pass) => pass.lowRisk)
          .concat(planningPasses.filter((pass) => !pass.lowRisk))
      : planningPasses;

  const recommendedPlan: PlanningPass[] = [];
  let accumulatedMinutes = 0;
  let accumulatedGigabytes = 0;

  for (const pass of planningQueue) {
    if (accumulatedMinutes > 0 && accumulatedMinutes + pass.minutes > sessionMinutes) {
      continue;
    }

    recommendedPlan.push(pass);
    accumulatedMinutes += pass.minutes;
    accumulatedGigabytes += pass.estimatedGb;

    if (accumulatedGigabytes >= targetSpaceGb) {
      break;
    }
  }

  const fallbackPlanningPass = planningQueue[0];

  if (recommendedPlan.length === 0 && fallbackPlanningPass) {
    recommendedPlan.push(fallbackPlanningPass);
    accumulatedMinutes = fallbackPlanningPass.minutes;
    accumulatedGigabytes = fallbackPlanningPass.estimatedGb;
  }

  const plannerOutcome =
    accumulatedGigabytes >= targetSpaceGb
      ? 'This target looks realistic in one cleanup session.'
      : 'You may need a second cleanup pass to reach this target.';

  const triageScoreMap = new Map(triageRanking.map((point) => [point.id, point.score]));
  const deviceRoutes = getDeviceCleanupRoutes(platform);
  const storageOverviewRoute = deviceRoutes.find((route) => route.id === 'settings-overview');
  const rankedDeviceRoutes = deviceRoutes
    .filter(
      (route): route is DeviceCleanupRoute & { id: PressurePointKey } =>
        route.id !== 'settings-overview',
    )
    .map((route) => {
      const baseScore = triageScoreMap.get(route.id) ?? 0;
      const selectionBoost = selectedPressurePoints.includes(route.id) ? 0.8 : 0;
      const duplicateBoost =
        route.id === 'duplicates'
          ? duplicateReviewSummary?.groupCount
            ? 3.2
            : scanSummary?.duplicateGroups.length
              ? 1.8
              : 0
          : 0;
      const screenshotBoost = route.id === 'screenshots' && scanSummary?.screenshotCount ? 2.4 : 0;
      const largeVideoBoost = route.id === 'large-videos' && scanSummary?.largeVideoCount ? 2.7 : 0;
      const downloadsBoost =
        route.id === 'downloads' && platform === 'android' && urgency !== 'steady' ? 1.1 : 0;

      return {
        ...route,
        score:
          baseScore +
          selectionBoost +
          duplicateBoost +
          screenshotBoost +
          largeVideoBoost +
          downloadsBoost,
      };
    })
    .sort((left, right) => right.score - left.score);
  const topDeviceRoutes = rankedDeviceRoutes.slice(0, 4);

  const getRouteReason = (route: DeviceCleanupRoute) => {
    if (route.id === 'duplicates' && duplicateReviewSummary?.groupCount) {
      return `You already found ${formatCount(duplicateReviewSummary.groupCount)} duplicate group${
        duplicateReviewSummary.groupCount === 1 ? '' : 's'
      }.`;
    }

    if (route.id === 'duplicates' && scanSummary?.duplicateGroups.length) {
      return `The file scan found ${formatCount(scanSummary.duplicateGroups.length)} copy group${
        scanSummary.duplicateGroups.length === 1 ? '' : 's'
      }.`;
    }

    if (route.id === 'screenshots' && scanSummary?.screenshotCount) {
      return `The file scan found ${formatCount(scanSummary.screenshotCount)} screenshot${
        scanSummary.screenshotCount === 1 ? '' : 's'
      }.`;
    }

    if (route.id === 'large-videos' && scanSummary?.largeVideoCount) {
      return `The file scan found ${formatCount(scanSummary.largeVideoCount)} large video${
        scanSummary.largeVideoCount === 1 ? '' : 's'
      }.`;
    }

    if (primaryFocus.id === route.id) {
      return 'This looks like the best first step right now.';
    }

    if (route.id !== 'settings-overview' && selectedPressurePoints.includes(route.id)) {
      return 'You marked this category as one of the most likely pressure points.';
    }

    return route.safety === 'low-risk'
      ? 'This is a good early step.'
      : 'Do this after the easy cleanup is done.';
  };

  const recommendedRouteLines = topDeviceRoutes.map(
    (route, index) => `${index + 1}. ${route.label} (${route.location})`,
  );
  const recommendedPlanLines = recommendedPlan.map(
    (pass, index) =>
      `${index + 1}. ${pass.label} - about ${formatStorage(pass.estimatedGb)} in ${pass.minutes} min`,
  );
  const cleanupChecklistText = [
    `${formatPlatformLabel(platform)} cleanup checklist`,
    `Urgency: ${urgencyLabels[urgency]}`,
    `Best first pass: ${primaryFocus.label}`,
    secondaryFocuses.length
      ? `Next categories: ${secondaryFocuses.map((focus) => focus.label).join(', ')}`
      : null,
    storageOverviewRoute ? `Check storage overview: ${storageOverviewRoute.location}` : null,
    '',
    'Open these places next:',
    ...recommendedRouteLines,
    '',
    scanSummary
      ? `Local media scan: ${formatCount(scanSummary.totalCount)} files, ${formatBytes(
          scanSummary.totalBytes,
        )}, focus on ${scanSummary.primaryFocus.toLowerCase()}.`
      : null,
    duplicateReviewSummary
      ? `Duplicate review: ${formatCount(
          duplicateReviewSummary.groupCount,
        )} groups, about ${formatBytes(
          duplicateReviewSummary.estimatedRecoverableBytes,
        )} potentially recoverable.`
      : null,
    '',
    'Suggested cleanup session:',
    ...recommendedPlanLines,
  ]
    .filter((line): line is string => Boolean(line))
    .join('\n');
  const checklistPreviewLines = cleanupChecklistText.split('\n').slice(0, 10);

  const primaryProjectHref = platform === 'iphone' ? '/cleanor-ios' : '/cleanor';
  const primaryProjectLabel =
    platform === 'iphone' ? 'Open Cleanor for iPhone' : 'Open Cleanor for Android';
  const secondaryGuideHref =
    platform === 'iphone'
      ? '/blog/how-to-free-up-space-on-iphone'
      : '/blog/best-android-cleaner-apps';
  const secondaryGuideLabel =
    platform === 'iphone' ? 'Read the iPhone cleanup guide' : 'Read the Android cleanup guide';

  const copyCleanupChecklist = async () => {
    setChecklistStatusMessage(null);
    setChecklistErrorMessage(null);

    try {
      if (!navigator.clipboard?.writeText) {
        throw new Error('This browser could not copy the checklist to the clipboard.');
      }

      await navigator.clipboard.writeText(cleanupChecklistText);
      setChecklistStatusMessage('Checklist copied.');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'The checklist could not be copied.';
      setChecklistErrorMessage(message);
    }
  };

  const handleScanFiles = async (event: ChangeEvent<HTMLInputElement>) => {
    const fileList = event.target.files;
    const selectedFiles = fileList ? Array.from(fileList) : [];

    if (selectedFiles.length === 0) {
      setScanSummary(null);
      setScanStatusMessage(null);
      setScanErrorMessage(null);
      return;
    }

    const supportedFiles = selectedFiles.filter(
      (file) => file.type.startsWith('image/') || file.type.startsWith('video/'),
    );

    if (supportedFiles.length === 0) {
      setScanSummary(null);
      setScanStatusMessage(null);
      setScanErrorMessage('Choose image or video files for the local media scan.');
      return;
    }

    const limitedFiles = supportedFiles.slice(0, maxScanFiles);
    setIsScanning(true);
    setScanSummary(null);
    setScanErrorMessage(null);
    setScanStatusMessage(
      supportedFiles.length > maxScanFiles
        ? `Scanning the first ${maxScanFiles} supported files from your selection.`
        : `Scanning ${supportedFiles.length} selected files locally in the browser.`,
    );

    try {
      const summary = await scanSelectedMediaFiles(limitedFiles);

      if (summary.totalCount === 0) {
        setScanSummary(null);
        setScanStatusMessage(null);
        setScanErrorMessage('The browser could not inspect any of the selected files.');
        return;
      }

      setScanSummary(summary);
      setScanStatusMessage(
        summary.skippedCount > 0
          ? `Scanned ${summary.totalCount} files locally and skipped ${summary.skippedCount} file${summary.skippedCount === 1 ? '' : 's'} that the browser could not inspect. Nothing was uploaded to a server.`
          : `Scanned ${summary.totalCount} selected files locally. Nothing was uploaded to a server.`,
      );
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'The browser could not scan these files.';
      setScanErrorMessage(message);
    } finally {
      setIsScanning(false);
    }
  };

  const clearScannedFiles = () => {
    setScanSummary(null);
    setScanStatusMessage(null);
    setScanErrorMessage(null);
    if (scanInputRef.current) {
      scanInputRef.current.value = '';
    }
  };

  const handleDuplicateReviewFiles = async (event: ChangeEvent<HTMLInputElement>) => {
    const fileList = event.target.files;
    const selectedFiles = fileList ? Array.from(fileList) : [];

    if (selectedFiles.length === 0) {
      setDuplicateReviewStatusMessage(null);
      setDuplicateReviewErrorMessage(null);
      setDuplicateReviewSummary(null);
      return;
    }

    const supportedFiles = selectedFiles.filter((file) => file.type.startsWith('image/'));

    if (supportedFiles.length === 0) {
      setDuplicateReviewStatusMessage(null);
      setDuplicateReviewSummary(null);
      setDuplicateReviewErrorMessage('Choose image files for duplicate photo review.');
      return;
    }

    const limitedFiles = supportedFiles.slice(0, maxDuplicateReviewFiles);
    setIsReviewingDuplicates(true);
    setDuplicateReviewStatusMessage(
      supportedFiles.length > maxDuplicateReviewFiles
        ? `Reviewing the first ${maxDuplicateReviewFiles} supported images from your selection.`
        : `Reviewing ${supportedFiles.length} selected images locally in the browser.`,
    );
    setDuplicateReviewErrorMessage(null);
    setDuplicateReviewSummary(null);

    try {
      const summary = await reviewDuplicatePhotos(limitedFiles);

      if (summary.totalCount === 0) {
        setDuplicateReviewStatusMessage(null);
        setDuplicateReviewErrorMessage('The browser could not inspect any of the selected images.');
        return;
      }

      setDuplicateReviewSummary(summary);
      setDuplicateReviewStatusMessage(
        summary.skippedCount > 0
          ? `Reviewed ${summary.totalCount} images locally and skipped ${summary.skippedCount} file${summary.skippedCount === 1 ? '' : 's'} that the browser could not inspect.`
          : `Reviewed ${summary.totalCount} images locally. Nothing was uploaded to a server.`,
      );
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : 'The browser could not complete duplicate photo review.';
      setDuplicateReviewErrorMessage(message);
    } finally {
      setIsReviewingDuplicates(false);
    }
  };

  const clearDuplicateReview = () => {
    setDuplicateReviewStatusMessage(null);
    setDuplicateReviewErrorMessage(null);
    setDuplicateReviewSummary(null);

    if (duplicateInputRef.current) {
      duplicateInputRef.current.value = '';
    }
  };

  return (
    <article className="project-detail-card discovery-detail-card storage-helper-shell">
      <div className="article-meta-strip discovery-meta-strip">
        <span>Local helper</span>
        <span>Simple steps</span>
      </div>

      <div className="storage-helper-header">
        <div className="storage-helper-header-copy">
          <h2>Start here</h2>
          <p>Pick your phone, choose what looks most likely, and get a simple cleanup order.</p>
        </div>

        <div className="storage-helper-tab-bar" role="tablist" aria-label="Storage cleanup modes">
          {helperModes.map((helperMode) => (
            <button
              aria-selected={mode === helperMode.id}
              className={`storage-helper-tab ${mode === helperMode.id ? 'is-active' : ''}`}
              key={helperMode.id}
              onClick={() => setMode(helperMode.id)}
              role="tab"
              type="button"
            >
              <strong>{helperMode.label}</strong>
              <span>{helperMode.description}</span>
            </button>
          ))}
        </div>
      </div>

      <section className="storage-helper-platform-grid" aria-label="Choose device">
        {platformOptions.map((option) => (
          <button
            className={`storage-helper-choice ${platform === option.id ? 'is-active' : ''}`}
            key={option.id}
            onClick={() => setPlatform(option.id)}
            type="button"
          >
            <strong>{option.label}</strong>
            {option.description ? <span>{option.description}</span> : null}
          </button>
        ))}
      </section>

      {mode === 'triage' ? (
        <section className="storage-helper-panel">
          <div className="storage-helper-grid">
            <article className="overview-card">
              <h3>How full is your phone?</h3>
              <div className="storage-helper-segmented">
                {urgencyOptions.map((option) => (
                  <button
                    className={`storage-helper-segment ${urgency === option.id ? 'is-active' : ''}`}
                    key={option.id}
                    onClick={() => setUrgency(option.id)}
                    type="button"
                  >
                    <strong>{option.label}</strong>
                    <span>{option.description}</span>
                  </button>
                ))}
              </div>
            </article>

            <article className="overview-card">
              <h3>What looks like the problem?</h3>
              <div className="storage-helper-chip-grid">
                {pressurePoints.map((point) => {
                  const isActive = selectedPressurePoints.includes(point.id);

                  return (
                    <button
                      aria-pressed={isActive}
                      className={`storage-helper-chip ${isActive ? 'is-active' : ''}`}
                      key={point.id}
                      onClick={() => togglePressurePoint(point.id)}
                      type="button"
                    >
                      <strong>{point.label}</strong>
                      <span>{point.description}</span>
                    </button>
                  );
                })}
              </div>
            </article>
          </div>

          <div className="storage-helper-summary-grid">
            <article className="overview-card storage-helper-result-card">
              <span className="eyebrow">Best first pass</span>
              <h3>{primaryFocus.label}</h3>
              <p>{triageIntro}</p>
              <p>{primaryFocus.actionLabel}</p>
            </article>

            <article className="overview-card">
              <h3>Do this order</h3>
              <ol className="discovery-step-list storage-helper-step-list">
                <li>{primaryFocus.label}</li>
                {secondaryFocuses.map((focus) => (
                  <li key={focus.id}>{focus.label}</li>
                ))}
                <li>{platform === 'iphone' ? 'Contacts last' : 'Final tidy-up'}</li>
              </ol>
            </article>

            <article className="overview-card">
              <h3>Avoid this</h3>
              <ul className="detail-list detail-list-compact">
                <li>Do not delete random photos first.</li>
                <li>Do not start with contacts.</li>
                <li>Do not ignore the biggest files.</li>
              </ul>
            </article>
          </div>
        </section>
      ) : null}

      {mode === 'estimate' ? (
        <section className="storage-helper-panel">
          <div className="storage-helper-grid">
            <article className="overview-card">
              <h3>Possible free space</h3>
              <p className="storage-helper-total">{formatStorage(totalEstimatedGigabytes)}</p>
              <p>This is a rough estimate to help you pick the best first step.</p>
            </article>

            <article className="overview-card storage-helper-result-card">
              <span className="eyebrow">Likely biggest win</span>
              <h3>{biggestEstimate.label}</h3>
              <p>
                This category could free about {formatStorage(biggestEstimate.estimatedGigabytes)}.
              </p>
              <p>{biggestEstimate.hint}</p>
            </article>
          </div>

          <div className="storage-helper-range-stack">
            {estimateDefinitions.map((definition) => (
              <label className="overview-card storage-helper-range-card" key={definition.id}>
                <div className="storage-helper-range-head">
                  <div>
                    <strong>{definition.label}</strong>
                    <p>{definition.hint}</p>
                  </div>
                  <span>
                    {formatCount(estimateInputs[definition.id])} {definition.unitLabel}
                  </span>
                </div>
                <input
                  max={definition.max}
                  min={definition.min}
                  onChange={(event) =>
                    setEstimateInputs((current) => ({
                      ...current,
                      [definition.id]: Number(event.target.value),
                    }))
                  }
                  step={definition.step}
                  type="range"
                  value={estimateInputs[definition.id]}
                />
                <div className="storage-helper-range-foot">
                  <span>
                    {formatStorage(
                      (estimateInputs[definition.id] *
                        (platform === 'iphone' ? definition.iphoneMb : definition.androidMb)) /
                        1024,
                    )}
                  </span>
                  <span>{platform === 'iphone' ? 'iPhone average' : 'Android average'}</span>
                </div>
              </label>
            ))}
          </div>

          <div className="storage-helper-breakdown-grid">
            {estimateBreakdown.map((item) => (
              <article className="overview-card" key={item.id}>
                <h3>{item.label}</h3>
                <p>{formatStorage(item.estimatedGigabytes)}</p>
              </article>
            ))}
          </div>
        </section>
      ) : null}

      {mode === 'scan' ? (
        <section className="storage-helper-panel">
          <div className="storage-helper-grid">
            <article className="overview-card">
              <h3>Scan a few files</h3>
              <p>Pick some photos or videos to see what looks biggest or repeated.</p>
              <label className="storage-helper-upload">
                <span>Select files</span>
                <input
                  accept="image/*,video/*"
                  multiple
                  onChange={handleScanFiles}
                  ref={scanInputRef}
                  type="file"
                />
              </label>
              <div className="storage-helper-upload-actions">
                <button className="button button-ghost" onClick={clearScannedFiles} type="button">
                  Clear
                </button>
              </div>
            </article>

            <article className="overview-card storage-helper-result-card">
              <span className="eyebrow">Private</span>
              <h3>Runs in your browser</h3>
              <ul className="detail-list detail-list-compact">
                <li>No upload.</li>
                <li>Good for a quick check.</li>
                <li>Best for small batches, not the full phone.</li>
              </ul>
            </article>
          </div>

          {scanStatusMessage ? <p className="storage-helper-note">{scanStatusMessage}</p> : null}
          {scanErrorMessage ? <p className="tool-error">{scanErrorMessage}</p> : null}
          {isScanning ? <p className="tool-progress">Scanning files...</p> : null}

          {scanSummary ? (
            <>
              <div className="storage-helper-summary-grid">
                <article className="overview-card">
                  <h3>Selected files</h3>
                  <p className="storage-helper-total">{formatBytes(scanSummary.totalBytes)}</p>
                  <p>
                    {formatCount(scanSummary.totalCount)} files •{' '}
                    {formatCount(scanSummary.imageCount)} images •{' '}
                    {formatCount(scanSummary.videoCount)} videos
                  </p>
                </article>

                <article className="overview-card storage-helper-result-card">
                  <span className="eyebrow">Best first pass</span>
                  <h3>{scanSummary.primaryFocus}</h3>
                  <p>{scanSummary.primaryReason}</p>
                  <p>{scanSummary.nextAction}</p>
                </article>
              </div>

              <div className="storage-helper-breakdown-grid">
                <article className="overview-card">
                  <h3>Screenshots</h3>
                  <p>
                    {formatCount(scanSummary.screenshotCount)} •{' '}
                    {formatBytes(scanSummary.screenshotBytes)}
                  </p>
                </article>
                <article className="overview-card">
                  <h3>Large videos</h3>
                  <p>
                    {formatCount(scanSummary.largeVideoCount)} •{' '}
                    {formatBytes(scanSummary.largeVideoBytes)}
                  </p>
                </article>
                <article className="overview-card">
                  <h3>Copy groups</h3>
                  <p>
                    {formatCount(scanSummary.duplicateGroups.length)} groups •{' '}
                    {formatBytes(scanSummary.possibleDuplicateBytes)}
                  </p>
                </article>
                <article className="overview-card">
                  <h3>Possible copies</h3>
                  <p>{formatCount(scanSummary.possibleDuplicateCount)} files</p>
                </article>
              </div>

              <div className="storage-helper-grid">
                <article className="overview-card">
                  <h3>Biggest files</h3>
                  <div className="storage-helper-file-list">
                    {scanSummary.topItems.map((item) => (
                      <div className="storage-helper-file-row" key={item.id}>
                        <div>
                          <strong>{item.fileName}</strong>
                          <span>
                            {item.kind === 'video' ? 'Video' : 'Image'} • {item.width} ×{' '}
                            {item.height}
                            {item.durationMs ? ` • ${formatDuration(item.durationMs)}` : ''}
                            {item.isLikelyScreenshot ? ' • screenshot' : ''}
                          </span>
                        </div>
                        <span>{formatBytes(item.size)}</span>
                      </div>
                    ))}
                  </div>
                </article>

                <article className="overview-card">
                  <h3>Possible copies</h3>
                  {scanSummary.duplicateGroups.length ? (
                    <div className="storage-helper-file-list">
                      {scanSummary.duplicateGroups.slice(0, 5).map((group) => (
                        <div className="storage-helper-file-row" key={group.key}>
                          <div>
                            <strong>{group.items[0]?.fileName}</strong>
                            <span>{formatCount(group.items.length)} similar files</span>
                          </div>
                          <span>{formatBytes(group.recoverableBytes)}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p>No clear copy groups were found in this batch.</p>
                  )}
                </article>
              </div>
            </>
          ) : null}
        </section>
      ) : null}

      {mode === 'duplicates' ? (
        <section className="storage-helper-panel">
          <div className="storage-helper-grid">
            <article className="overview-card">
              <h3>Find duplicate photos</h3>
              <p>Pick some photos and this page will group ones that look the same.</p>
              <label className="storage-helper-upload">
                <span>Select photos</span>
                <input
                  accept="image/*"
                  multiple
                  onChange={handleDuplicateReviewFiles}
                  ref={duplicateInputRef}
                  type="file"
                />
              </label>
              <div className="storage-helper-upload-actions">
                <button
                  className="button button-ghost"
                  onClick={clearDuplicateReview}
                  type="button"
                >
                  Clear
                </button>
              </div>
            </article>

            <article className="overview-card storage-helper-result-card">
              <span className="eyebrow">Private</span>
              <h3>Runs in your browser</h3>
              <ul className="detail-list detail-list-compact">
                <li>No upload.</li>
                <li>Works on the photos you pick.</li>
                <li>Best for repeated shots and copies.</li>
              </ul>
            </article>
          </div>

          {duplicateReviewStatusMessage ? (
            <p className="storage-helper-note">{duplicateReviewStatusMessage}</p>
          ) : null}
          {duplicateReviewErrorMessage ? (
            <p className="tool-error">{duplicateReviewErrorMessage}</p>
          ) : null}
          {isReviewingDuplicates ? <p className="tool-progress">Checking photos...</p> : null}

          {duplicateReviewSummary ? (
            <>
              <div className="storage-helper-summary-grid">
                <article className="overview-card">
                  <h3>Groups found</h3>
                  <p className="storage-helper-total">
                    {formatCount(duplicateReviewSummary.groupCount)}
                  </p>
                  <p>
                    {formatCount(duplicateReviewSummary.groupedItemCount)} grouped images from{' '}
                    {formatCount(duplicateReviewSummary.totalCount)} reviewed
                  </p>
                </article>

                <article className="overview-card storage-helper-result-card">
                  <span className="eyebrow">Possible free space</span>
                  <h3>{formatBytes(duplicateReviewSummary.estimatedRecoverableBytes)}</h3>
                  <p>Keep one photo from each group and review the rest.</p>
                </article>
              </div>

              <div className="storage-helper-breakdown-grid">
                <article className="overview-card">
                  <h3>Reviewed images</h3>
                  <p>{formatCount(duplicateReviewSummary.totalCount)}</p>
                </article>
                <article className="overview-card">
                  <h3>Grouped images</h3>
                  <p>{formatCount(duplicateReviewSummary.groupedItemCount)}</p>
                </article>
                <article className="overview-card">
                  <h3>Duplicate groups</h3>
                  <p>{formatCount(duplicateReviewSummary.groupCount)}</p>
                </article>
                <article className="overview-card">
                  <h3>Skipped images</h3>
                  <p>{formatCount(duplicateReviewSummary.skippedCount)}</p>
                </article>
              </div>

              <div className="storage-helper-duplicate-groups">
                {duplicateReviewSummary.groups.length ? (
                  duplicateReviewSummary.groups.slice(0, 8).map((group, groupIndex) => (
                    <article className="overview-card storage-helper-duplicate-card" key={group.id}>
                      <div className="storage-helper-plan-head">
                        <h3>Group {groupIndex + 1}</h3>
                        <span>{formatBytes(group.estimatedRecoverableBytes)}</span>
                      </div>
                      <div className="storage-helper-thumb-grid">
                        {group.items.map((item, itemIndex) => (
                          <figure className="storage-helper-thumb-card" key={item.id}>
                            <img
                              alt={item.fileName}
                              className="storage-helper-thumb"
                              loading="lazy"
                              src={item.previewUrl}
                            />
                            <figcaption>
                              <strong>{itemIndex === 0 ? 'Keep' : 'Review'}</strong>
                              <span>{item.fileName}</span>
                              <span>
                                {item.width} × {item.height} • {formatBytes(item.size)}
                              </span>
                            </figcaption>
                          </figure>
                        ))}
                      </div>
                    </article>
                  ))
                ) : (
                  <article className="overview-card">
                    <h3>No duplicate groups found</h3>
                    <p>This batch did not show obvious duplicate photos.</p>
                  </article>
                )}
              </div>
            </>
          ) : null}
        </section>
      ) : null}

      {mode === 'routes' ? (
        <section className="storage-helper-panel">
          <div className="storage-helper-grid">
            <article className="overview-card storage-helper-result-card">
              <span className="eyebrow">Open this first</span>
              <h3>{storageOverviewRoute?.label ?? 'Check storage overview first'}</h3>
              <p>{storageOverviewRoute?.location}</p>
              <p>{storageOverviewRoute?.detail}</p>
            </article>

            <article className="overview-card">
              <h3>Next places to open</h3>
              <ol className="discovery-step-list storage-helper-step-list">
                {topDeviceRoutes.slice(0, 3).map((route) => (
                  <li key={route.id}>
                    {route.label} • {route.location}
                  </li>
                ))}
              </ol>
            </article>
          </div>

          <div className="storage-helper-plan-grid">
            {topDeviceRoutes.map((route) => (
              <article className="overview-card storage-helper-route-card" key={route.id}>
                <div className="storage-helper-plan-head">
                  <h3>{route.label}</h3>
                  <span>{route.safety === 'low-risk' ? 'Easy first' : 'Review later'}</span>
                </div>
                <p className="storage-helper-route-location">{route.location}</p>
                <p>{route.detail}</p>
                <p>{getRouteReason(route)}</p>
              </article>
            ))}
          </div>
        </section>
      ) : null}

      {mode === 'plan' ? (
        <section className="storage-helper-panel">
          <div className="storage-helper-grid">
            <article className="overview-card">
              <h3>How much time do you have?</h3>
              <div className="storage-helper-inline-choices">
                {[10, 20, 40, 60].map((minutes) => (
                  <button
                    className={`storage-helper-inline-choice ${
                      sessionMinutes === minutes ? 'is-active' : ''
                    }`}
                    key={minutes}
                    onClick={() => setSessionMinutes(minutes)}
                    type="button"
                  >
                    {minutes} min
                  </button>
                ))}
              </div>

              <h3>How much space do you want back?</h3>
              <label className="storage-helper-slider">
                <span>{targetSpaceGb} GB</span>
                <input
                  max={12}
                  min={1}
                  onChange={(event) => setTargetSpaceGb(Number(event.target.value))}
                  step={1}
                  type="range"
                  value={targetSpaceGb}
                />
              </label>
            </article>

            <article className="overview-card">
              <h3>Choose a style</h3>
              <div className="storage-helper-segmented">
                {planningStyles.map((style) => (
                  <button
                    className={`storage-helper-segment ${
                      planningStyle === style.id ? 'is-active' : ''
                    }`}
                    key={style.id}
                    onClick={() => setPlanningStyle(style.id)}
                    type="button"
                  >
                    <strong>{style.label}</strong>
                    <span>{style.description}</span>
                  </button>
                ))}
              </div>
            </article>
          </div>

          <div className="storage-helper-summary-grid">
            <article className="overview-card storage-helper-result-card">
              <span className="eyebrow">Expected result</span>
              <h3>
                {formatStorage(accumulatedGigabytes)} in about {accumulatedMinutes} minutes
              </h3>
              <p>{plannerOutcome}</p>
            </article>

            <article className="overview-card">
              <h3>Suggested order</h3>
              <ol className="discovery-step-list storage-helper-step-list">
                {recommendedPlan.map((pass) => (
                  <li key={pass.id}>
                    {pass.label} • about {formatStorage(pass.estimatedGb)} • {pass.minutes} min
                  </li>
                ))}
              </ol>
            </article>
          </div>

          <div className="storage-helper-plan-grid">
            {recommendedPlan.map((pass) => (
              <article className="overview-card" key={pass.id}>
                <div className="storage-helper-plan-head">
                  <h3>{pass.label}</h3>
                  <span>
                    {pass.minutes} min • {formatStorage(pass.estimatedGb)}
                  </span>
                </div>
                <p>{pass.detail}</p>
              </article>
            ))}
          </div>
        </section>
      ) : null}

      <section className="overview-card storage-helper-checklist-card">
        <div className="storage-helper-plan-head">
          <h3>Copy checklist</h3>
          <span>{formatPlatformLabel(platform)}</span>
        </div>
        <div className="storage-helper-checklist-preview" aria-label="Cleanup checklist preview">
          {checklistPreviewLines.map((line, index) => (
            <p key={`${line}-${index}`}>{line || '\u00A0'}</p>
          ))}
        </div>
        <div className="storage-helper-copy-actions">
          <button className="button" onClick={() => void copyCleanupChecklist()} type="button">
            Copy
          </button>
          <button className="button button-ghost" onClick={() => setMode('routes')} type="button">
            Open steps
          </button>
        </div>
        {checklistStatusMessage ? <p className="tool-progress">{checklistStatusMessage}</p> : null}
        {checklistErrorMessage ? <p className="tool-error">{checklistErrorMessage}</p> : null}
      </section>

      <section className="storage-helper-route-grid">
        <Link className="button" href={primaryProjectHref}>
          {primaryProjectLabel}
        </Link>
        <Link className="button button-ghost" href={secondaryGuideHref}>
          {secondaryGuideLabel}
        </Link>
      </section>
    </article>
  );
}
